import React from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  Typography,
  useTheme,
} from '@mui/material';
import { SignInErrorType } from '../../pages/Auth/reducer';
import { OtpErrorType } from '../../pages/Auth/actions';
import {
  GlassTextField,
  GlassButton,
  SectionHeader,
  BodyText,
  GlassCard,
  GlassColors,
  ButtonTertiary,
} from '../glassmorphism/GlassmorphismStyles';
import RailStepper from '../../components/RailStepper/RailStepper';

interface PassedProps {
  email: string;
  onSubmit: (code: string) => void;
  onBack: () => void;
  isLoading: boolean;
  errorType?: SignInErrorType | OtpErrorType;
  isHandyman: boolean;
}

const EnterOtpCodeStep = (props: PassedProps) => {
  const [codeError, setCodeError] = React.useState(false);
  const [codeErrorMessage, setCodeErrorMessage] = React.useState('');

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  React.useEffect(() => {
    if (props.errorType === OtpErrorType.InvalidCode) {
      setCodeError(true);
      setCodeErrorMessage('Incorrect OTP code. Please try again.');
    } else if (props.errorType === OtpErrorType.CodeExpired) {
      setCodeError(true);
      setCodeErrorMessage('OTP code has expired. Please request a new code.');
    }
  }, [props.errorType]);

  const validateInputs = () => {
    const code = document.getElementById('code') as HTMLInputElement;

    let isValid = true;

    if (!code.value || code.value.length !== 6) {
      setCodeError(true);
      setCodeErrorMessage(
        'Please enter the complete 6-digit verification code.',
      );
      isValid = false;
    } else if (!/^[0-9]{6}$/.test(code.value)) {
      setCodeError(true);
      setCodeErrorMessage('Verification code must contain only numbers.');
      isValid = false;
    } else {
      setCodeError(false);
      setCodeErrorMessage('');
    }

    return isValid;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (codeError) {
      return;
    }

    const data = new FormData(event.currentTarget);

    props.onSubmit(data.get('code') as string);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '16px', sm: '24px' },
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: { xs: '208px', sm: 'unset' },
          m: 'auto',
        }}
      >
        <SectionHeader
          variant='h4'
          sx={{
            fontSize: { xs: '20px', sm: '24px' },
            lineHeight: { xs: '160%', sm: '133%' },
            letterSpacing: { xs: '.15px', sm: 0 },
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: { xs: 800, sm: 700 },
            filter: 'drop-shadow(0px -0.25px 0.4px #0000008C)',
            color: isDark
              ? GlassColors.dark.accent
              : GlassColors.light.secondary,
            mb: '8px',
          }}
        >
          Verify Your Email
        </SectionHeader>
        <BodyText
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 700,
            lineHeight: { xs: '157%', sm: '175%' },
            letterSpacing: { xs: '.1px', sm: '.15px' },
            mx: 'auto',
            color: isDark
              ? GlassColors.dark.textPrimary
              : GlassColors.light.textPrimary,
          }}
        >
          We've sent a verification code to your email address
        </BodyText>
      </Box>

      {/* Email Display Card */}
      <GlassCard
        sx={{
          p: '24px',
          textAlign: 'center',
          borderRadius: '32px',
          boxShadow: `
            0px 0px 10px 0px #0000000A,
            0px -0.5px 0.25px 0px #0000004D,
            0px 0.5px 0.75px 0px #FFFFFF,
            0px 2.07px 3.73px 0px #00000045 inset,
            0px 1.08px 2.48px 0px #00000040 inset
          `,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: { xs: 700, sm: 800 },
            fontSize: { xs: '16px', sn: '20px' },
            lineHeight: { xs: '175%', tablet: '160%' },
            letterSpacing: '.15px',
            color: isDark
              ? GlassColors.dark.accent
              : GlassColors.dark.secondary,
            mb: 1,
          }}
        >
          Verification code sent to:
        </Typography>
        <Typography
          sx={{
            fontFamily: 'roboto, sans-serif',
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: { xs: '157%', sm: '175%' },
            letterSpacing: { xs: '.1px', sm: '.15px' },
            fontWeight: 700,
            color: isDark
              ? GlassColors.dark.textPrimary
              : GlassColors.light.textPrimary,
            wordBreak: 'break-all',
          }}
        >
          {props.email}
        </Typography>
      </GlassCard>

      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <FormControl>
          <FormLabel
            htmlFor='code'
            sx={{
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500,
              mb: 1,
            }}
          >
            Enter 6-digit verification code
          </FormLabel>
          <GlassTextField
            autoComplete='off'
            name='code'
            required
            fullWidth
            id='code'
            placeholder='------'
            error={codeError}
            helperText={codeErrorMessage}
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]{6}',
              style: {
                fontSize: '12px',
                letterSpacing: '0.5em',
                fontFamily: 'montserrat, sans-serif',
              },
            }}
            onChange={(e) => {
              // Only allow numbers
              const value = e.target.value.replace(/[^0-9]/g, '');
              e.target.value = value;
              if (codeError && value.length > 0) {
                setCodeError(false);
                setCodeErrorMessage('');
              }
            }}
          />
        </FormControl>
        {/* Help Text */}
        <Box sx={{ textAlign: 'center', my: 2 }}>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '12px',
              lineHeight: '166%',
              letterSpacing: '.4px',
              color: isDark
                ? GlassColors.dark.textSecondary
                : GlassColors.light.textSecondary,
              position: 'relative',
              mb: '6px',
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                height: '1px',
                top: '50%',
                width: '100%',
                background: isDark
                  ? GlassColors.dark.textSecondary
                  : GlassColors.light.textSecondary,
              },
              '& span': {
                padding: '0 10px',
                position: 'relative',
                zIndex: 1,
                background: isDark
                  ? GlassColors.dark.primaryBackground
                  : GlassColors.light.primaryBackground,
              },
            }}
          >
            <span>Didn't receive the code?</span>
          </Typography>
          <ButtonTertiary
            onClick={() => {
              // TODO: Implement resend functionality
              alert('Resend functionality not implemented yet');
            }}
            disabled={props.isLoading}
          >
            Resend Code
          </ButtonTertiary>
        </Box>
        <RailStepper isProviderFlow={props.isHandyman} activeIndex={1} />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mt: { xs: '4px', sm: 2 },
            justifyContent: 'space-between',
          }}
        >
          <ButtonTertiary onClick={props.onBack} disabled={props.isLoading}>
            Back
          </ButtonTertiary>
          <GlassButton
            type='submit'
            onClick={validateInputs}
            disabled={props.isLoading}
            sx={{ fontSize: '12px' }}
          >
            {props.isLoading ? (
              <CircularProgress size={20} style={{ color: 'white' }} />
            ) : props.isHandyman ? (
              'Verify & Go To Next Step'
            ) : (
              'Verify & Complete'
            )}
          </GlassButton>
        </Box>
      </Box>
    </Box>
  );
};

export default EnterOtpCodeStep;
